import { createApiInstance, LONG_TIMEOUT_CONFIG } from './config'

const api = createApiInstance()
const uploadApi = createApiInstance(LONG_TIMEOUT_CONFIG) // 用于文件上传的长超时实例
const API_BASE_URL = '/admin/game-items'

/**
 * 游戏道具管理API
 */
export const gameItemsAPI = {
  /**
   * 获取游戏道具列表
   * @param {string} game 游戏名称
   * @returns {Promise} API响应
   */
  getGameItems(game) {
    return api.get(API_BASE_URL, {
      params: { game }
    })
  },

  /**
   * 批量上传道具图标
   * @param {string} game 游戏名称
   * @param {Array} files 文件列表，每个文件对象包含 { file: File, backgroundColor?: string }
   * @returns {Promise} API响应
   */
  uploadItemIcons(game, files) {
    const formData = new FormData()
    formData.append('game', game)
    
    // 添加多个文件
    const backgroundColors = []
    for (let i = 0; i < files.length; i++) {
      const fileInfo = files[i]
      const file = fileInfo.file || fileInfo
      formData.append('files', file)
      
      // 收集背景色（如果有）
      if (fileInfo.backgroundColor) {
        backgroundColors.push(fileInfo.backgroundColor)
      } else {
        backgroundColors.push('')
      }
    }
    
    // 添加背景色数组
    backgroundColors.forEach((color, index) => {
      if (color) {
        formData.append(`background_colors[${index}]`, color)
      }
    })
    
    return uploadApi.post(`${API_BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  /**
   * 更新道具状态
   * @param {number} id 道具ID
   * @param {number} status 状态值
   * @returns {Promise} API响应
   */
  updateItemStatus(id, status) {
    return api.put(`${API_BASE_URL}/${id}/status`, null, {
      params: { status }
    })
  },

  /**
   * 更新道具仓库可见性
   * @param {number} id 道具ID
   * @param {number} isWarehouse 仓库可见值
   * @returns {Promise} API响应
   */
  updateItemWarehouse(id, isWarehouse) {
    return api.put(`${API_BASE_URL}/${id}/warehouse`, null, {
      params: { is_warehouse: isWarehouse }
    })
  },

  /**
   * 更新道具删除可见性
   * @param {number} id 道具ID
   * @param {number} isDelete 删除可见值
   * @returns {Promise} API响应
   */
  updateItemDelete(id, isDelete) {
    return api.put(`${API_BASE_URL}/${id}/delete`, null, {
      params: { is_delete: isDelete }
    })
  },

  /**
   * 更新道具名称
   * @param {number} id 道具ID
   * @param {string} itemName 新的道具名称
   * @returns {Promise} API响应
   */
  updateItemName(id, itemName) {
    return api.put(`${API_BASE_URL}/${id}/name`, null, {
      params: { item_name: itemName }
    })
  },

  /**
   * 更新道具使用可见性
   * @param {number} id 道具ID
   * @param {number} isUse 使用可见值
   * @returns {Promise} API响应
   */
  updateItemUse(id, isUse) {
    return api.put(`${API_BASE_URL}/${id}/use`, null, {
      params: { is_use: isUse }
    })
  },

  /**
   * 更新道具背景色
   * @param {number} id 道具ID
   * @param {string} backgroundColor 背景色：light-green(浅绿)、light-blue(浅蓝)、light-purple(浅紫)
   * @returns {Promise} API响应
   */
  updateItemBackgroundColor(id, backgroundColor) {
    return api.put(`${API_BASE_URL}/${id}/background-color`, null, {
      params: { background_color: backgroundColor }
    })
  },

  /**
   * 复制道具
   * @param {number} id 道具ID
   * @returns {Promise} API响应
   */
  copyItem(id) {
    return api.post(`${API_BASE_URL}/${id}/copy`)
  },

  /**
   * 删除道具
   * @param {number} id 道具ID
   * @returns {Promise} API响应
   */
  deleteItem(id) {
    return api.delete(`${API_BASE_URL}/${id}`)
  },

  /**
   * 批量更新状态
   * @param {Array<number>} ids ID列表
   * @param {number} status 状态值
   * @returns {Promise} API响应
   */
  batchUpdateStatus(ids, status) {
    return api.put(`${API_BASE_URL}/batch/status`, null, {
      params: { 
        ids: ids.join(','),
        status 
      }
    })
  },

  /**
   * 批量更新仓库可见性
   * @param {Array<number>} ids ID列表
   * @param {number} isWarehouse 仓库可见值
   * @returns {Promise} API响应
   */
  batchUpdateWarehouse(ids, isWarehouse) {
    return api.put(`${API_BASE_URL}/batch/warehouse`, null, {
      params: { 
        ids: ids.join(','),
        is_warehouse: isWarehouse 
      }
    })
  },

  /**
   * 批量更新删除可见性
   * @param {Array<number>} ids ID列表
   * @param {number} isDelete 删除可见值
   * @returns {Promise} API响应
   */
  batchUpdateDelete(ids, isDelete) {
    return api.put(`${API_BASE_URL}/batch/delete`, null, {
      params: { 
        ids: ids.join(','),
        is_delete: isDelete 
      }
    })
  },

  /**
   * 批量更新使用可见性
   * @param {Array<number>} ids ID列表
   * @param {number} isUse 使用可见值
   * @returns {Promise} API响应
   */
  batchUpdateUse(ids, isUse) {
    return api.put(`${API_BASE_URL}/batch/use`, null, {
      params: { 
        ids: ids.join(','),
        is_use: isUse 
      }
    })
  },

  /**
   * 批量删除道具
   * @param {Array<number>} ids ID列表
   * @returns {Promise} API响应
   */
  batchDelete(ids) {
    return api.delete(`${API_BASE_URL}/batch`, {
      params: { 
        ids: ids.join(',')
      }
    })
  }
}

export default gameItemsAPI
